import React from 'react';
import PageLayout from '../components/layout/PageLayout';
import HelmetWrapper from '../components/common/HelmetWrapper';

const TermsOfService = () => {
  return (
    <PageLayout>
      <HelmetWrapper>
        <title>Terms of Service - PakPoints</title>
        <meta name="description" content="Terms of Service for PakPoints - Read our terms and conditions for using our platform." />
        <meta property="og:title" content="Terms of Service - PakPoints" />
        <meta property="og:description" content="Terms of Service for PakPoints - Read our terms and conditions for using our platform." />
      </HelmetWrapper>

      <div className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Service</h1>
            
            <div className="prose max-w-none">
              <p className="text-gray-600 mb-6">
                <strong>Last updated:</strong> {new Date().toLocaleDateString()}
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-700 mb-4">
                  By accessing and using PakPoints ("the Service"), you accept and agree to be bound by the terms 
                  and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
                <p className="text-gray-700 mb-4">
                  PakPoints is a community platform that provides:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Society listings and information</li>
                  <li>Business directories and reviews</li>
                  <li>Property listings and real estate information</li>
                  <li>Event listings and community announcements</li>
                  <li>Free item exchanges and marketplace</li>
                  <li>Educational institute directories</li>
                  <li>Mosque and religious place listings</li>
                  <li>Community forums and discussions</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Account Creation</h3>
                <p className="text-gray-700 mb-4">
                  To access certain features of the Service, you must register for an account. You agree to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and update your account information</li>
                  <li>Keep your password secure and confidential</li>
                  <li>Accept responsibility for all activities under your account</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Account Termination</h3>
                <p className="text-gray-700 mb-4">
                  We reserve the right to suspend or terminate your account at any time for violations of these terms 
                  or for any other reason at our sole discretion.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. User Content and Conduct</h2>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Content Guidelines</h3>
                <p className="text-gray-700 mb-4">
                  You are responsible for all content you post, upload, or share on the Service. You agree not to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Post false, misleading, or fraudulent information</li>
                  <li>Upload content that violates any laws or regulations</li>
                  <li>Post spam, advertisements, or promotional content without permission</li>
                  <li>Harass, abuse, or harm other users</li>
                  <li>Upload malicious software or viruses</li>
                  <li>Violate intellectual property rights</li>
                  <li>Post content that is defamatory, obscene, or offensive</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Content Moderation</h3>
                <p className="text-gray-700 mb-4">
                  We reserve the right to review, edit, or remove any content that violates these terms. 
                  We may also suspend or ban users who repeatedly violate our guidelines.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Intellectual Property Rights</h2>
                <p className="text-gray-700 mb-4">
                  The Service and its original content, features, and functionality are owned by PakPoints and are 
                  protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
                </p>
                <p className="text-gray-700 mb-4">
                  You retain ownership of content you post, but grant us a license to use, display, and distribute 
                  your content in connection with the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Prohibited Uses</h2>
                <p className="text-gray-700 mb-4">
                  You may not use our Service:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
                  <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
                  <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
                  <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
                  <li>To submit false or misleading information</li>
                  <li>To upload or transmit viruses or any other type of malicious code</li>
                  <li>To spam, phish, pharm, pretext, spider, crawl, or scrape</li>
                  <li>For any obscene or immoral purpose</li>
                  <li>To interfere with or circumvent the security features of the Service</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Privacy Policy</h2>
                <p className="text-gray-700 mb-4">
                  Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Service, 
                  to understand our practices.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Disclaimers and Limitation of Liability</h2>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">8.1 Service Availability</h3>
                <p className="text-gray-700 mb-4">
                  We strive to provide continuous service availability, but we do not guarantee that the Service will be 
                  available at all times. We may experience hardware, software, or other problems or need to perform 
                  maintenance related to the Service.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">8.2 Content Accuracy</h3>
                <p className="text-gray-700 mb-4">
                  We do not guarantee the accuracy, completeness, or usefulness of any information on the Service. 
                  We are not responsible for any user-generated content or third-party content.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">8.3 Limitation of Liability</h3>
                <p className="text-gray-700 mb-4">
                  In no event shall PakPoints, nor its directors, employees, partners, agents, suppliers, or affiliates, 
                  be liable for any indirect, incidental, special, consequential, or punitive damages, including without 
                  limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use 
                  of the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Indemnification</h2>
                <p className="text-gray-700 mb-4">
                  You agree to defend, indemnify, and hold harmless PakPoints and its licensee and licensors, and their 
                  employees, contractors, agents, officers and directors, from and against any and all claims, damages, 
                  obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees).
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Termination</h2>
                <p className="text-gray-700 mb-4">
                  We may terminate or suspend your account and bar access to the Service immediately, without prior notice 
                  or liability, under our sole discretion, for any reason whatsoever and without limitation, including but 
                  not limited to a breach of the Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Governing Law</h2>
                <p className="text-gray-700 mb-4">
                  These Terms shall be interpreted and governed by the laws of Pakistan, without regard to its conflict 
                  of law provisions. Our failure to enforce any right or provision of these Terms will not be considered 
                  a waiver of those rights.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
                <p className="text-gray-700 mb-4">
                  We reserve the right, at our sole discretion, to modify or replace these Terms at any time. 
                  If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Information</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 mb-2">
                    <strong>Email:</strong> legal@pakpoints.com
                  </p>
                  <p className="text-gray-700 mb-2">
                    <strong>Website:</strong> <a href="https://www.pakpoints.com" className="text-blue-600 hover:underline">www.pakpoints.com</a>
                  </p>
                  <p className="text-gray-700">
                    <strong>Address:</strong> Pakistan
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default TermsOfService;
